import React, { useEffect, useState } from "react";
import {
  clienteleData,
  clienteleTabletData,
  clienteleMobileData,
  clienteleImageUrl,
} from "../../../utils/constants";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import scriptLoader from "../../../utils/javascriptsLoader";
import "./Clientele.css";
import PropTypes from "prop-types";
import { useMediaQuery } from "react-responsive";

// Sprite image renderer
const ClienteleSprite = ({ className, alt, spriteUrl }) => (
  <img className={className} src={spriteUrl} alt={alt} loading="lazy" />
);

ClienteleSprite.propTypes = {
  className: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  spriteUrl: PropTypes.string.isRequired,
};

// Slider
const ClienteleSlider = ({ slides }) => {
  const [loadedSprites, setLoadedSprites] = useState([true, ...Array(slides.length - 1).fill(false)]);

  useEffect(() => {
    const $slider = window.$("#clientele_slider");

    const handleSlideChange = (event) => {
      const currentIndex = event.item.index;
      const visibleSlide = currentIndex % slides.length;

      if (!loadedSprites[visibleSlide]) {
        setLoadedSprites((prev) => {
          const updated = [...prev];
          updated[visibleSlide] = true;
          return updated;
        });
      }
    };

    $slider.on("changed.owl.carousel", handleSlideChange);
    return () => $slider.off("changed.owl.carousel", handleSlideChange);
  }, [slides.length, loadedSprites]);

  return (
    <div id="clientele_slider" className="owl-carousel">
      {slides.map((slide, slideIndex) => {
        const spriteUrl = loadedSprites[slideIndex] ? clienteleImageUrl[slideIndex] : "";

        return (
          <div key={slideIndex} className="slide-container-cli">
            <div className="image-row-cli row-1">
              {slide.row_1_images.map((image, idx) => (
                <div key={idx} className="clienteleImageContainer">
                  {spriteUrl ? (
                    <ClienteleSprite
                      className={`${image.className} high_res_image`}
                      alt={image.alt}
                      spriteUrl={spriteUrl}
                    />
                  ) : (
                    <Skeleton className={image.className} height={50} width={50} />
                  )}
                </div>
              ))}
            </div>
            <div className="image-row-cli row-2">
              {slide.row_2_images.map((image, idx) => (
                <div key={idx} className="clienteleImageContainer">
                  {spriteUrl ? (
                    <ClienteleSprite
                      className={`${image.className} high_res_image`}
                      alt={image.alt}
                      spriteUrl={spriteUrl}
                    />
                  ) : (
                    <Skeleton className={image.className} height={50} width={50} />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};


ClienteleSlider.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      row_1_images: PropTypes.arrayOf(
        PropTypes.shape({
          className: PropTypes.string.isRequired,
          alt: PropTypes.string.isRequired,
        })
      ).isRequired,
      row_2_images: PropTypes.arrayOf(
        PropTypes.shape({
          className: PropTypes.string.isRequired,
          alt: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
};

// Layout selector
const getLayoutData = (layout) => {
  switch (layout) {
    case "mobile":
      return clienteleMobileData;
    case "tablet":
      return clienteleTabletData;
    default:
      return clienteleData;
  }
};

// Main component
const Clientele = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const isTablet = useMediaQuery({ maxWidth: 768 });
  const isMobile = useMediaQuery({ maxWidth: 480 });
  const layout = isMobile ? "mobile" : isTablet ? "tablet" : "desktop";

  useEffect(() => {
    const loadClienteleJs = () => {
      const clienteleScript = {
        src: `${process.env.REACT_APP_BASE_URL}js/modules/clientele.js?ca93809a-cd54-40d0-ad2c-a763aba41895`,
        async: 1,
        defer: 1,
        id: "clienteleJsId",
      };
      scriptLoader(clienteleScript, () => {
        if (window.lazyLoadInstance) window.lazyLoadInstance.update();
        setLoading(false);
      });
    };

    if (window.$ && window.$("#clientele_slider").data("owl.carousel")) {
      window.$("#clientele_slider").trigger("destroy.owl.carousel");
    }

    loadClienteleJs();
  }, [layout]);

  if (loading) {
    return (
      <section id="clientele">
        <div className="container-fluid text-center">
          <div className="col-md-12">
            <div className="gradient-text-default title">
              <Skeleton height={40} width="30%" />
            </div>
          </div>
          <div className="clientele-container" id="clientele-sec">
            <div className="clientele-left" style={{ display: "inline" }}>
              <Skeleton height={100} width="80%" count={2} />
            </div>
            <div className="clientele-right">
              <Skeleton height={200} width="80%" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="trusted-by-leading-brands">
      <div className="container-fluid fadeIn text-center">
        <div className="col-md-12">
          <div className="title d-inline-block">
            <h2 className="gradient-text-default">{t("aboutus.title2")}</h2>
          </div>
        </div>
        <div className="clientele-container">
          <div className="clientele-left" key={layout}>
            <ClienteleSlider slides={getLayoutData(layout)} />
          </div>
          <div className="clientele-right">
            <img
              src="/images/clientele/Group_1000001929.webp"
              alt="clientele-img"
              style={{ width: "80%", height: "100%" }}
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Clientele;